import moment from 'moment';
import isMobile from '../../../utils/isMobile';

// OTG 2023 - Tom Hedges

export enum CalendarType {
  Google = 'Google',
  Apple = 'Apple',
  Outlook = 'Outlook',
  OutlookWeb = 'Outlook-Web',
  Yahoo = 'Yahoo',
}

export interface AddToCalendarEvent {
  title: string;
  description: string;
  location: string;
  startTime: Date;
  endTime: Date;
}

const isMobileUserAgent =
  typeof window !== 'undefined' ? isMobile(window) : false;

const formatTime = (date: Date) => {
  return moment.utc(date).format('YYYYMMDD');
};

const formatEndTime = (endDate: Date, startDate: Date) => {
  if (endDate.toDateString() !== startDate.toDateString()) {
    return formatTime(endDate);
  }
  return moment
    .utc(startDate)
    .add(1, 'days')
    .format('YYYYMMDD');
};

const getRandomKey = () => {
  const rand = Math.floor(Math.random() * 999999999999).toString();
  return new Date().getTime().toString() + '_' + rand;
};

const buildEventUrl = (event: AddToCalendarEvent, type: CalendarType) => {
  let calendarUrl = '';
  const formattedStartTime = formatTime(event.startTime);
  const formattedEndTime = formatEndTime(event.endTime, event.startTime);
  const encodedLocation = encodeURIComponent(event.location);
  const encodedTitle = encodeURIComponent(event.title);
  const encodedDescription = encodeURIComponent(event.description);

  switch (type) {
    case CalendarType.Google:
      calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&dates=${formattedStartTime}/${formattedEndTime}&location=${encodedLocation}&text=${encodedTitle}&details=${encodedDescription}&transparency=transparent`;
      break;
    case CalendarType.Yahoo:
      calendarUrl = `https://calendar.yahoo.com/?v=60&view=d&type=20&title=${encodedTitle}&st=${formattedStartTime}&et=${formattedEndTime}&desc=${encodedDescription}&in_loc=${encodedLocation}`;
      break;
    case CalendarType.OutlookWeb:
      calendarUrl = `https://outlook.live.com/owa/?rru=addevent&startdt=${formattedStartTime}&enddt=${formattedEndTime}&subject=${encodedTitle}&location=${encodedLocation}&body=${encodedDescription}&allday=false&uid=${getRandomKey()}&path=/calendar/view/Month`;
      break;
    default:
      calendarUrl = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'BEGIN:VEVENT',
        'DTSTART:' + formattedStartTime,
        'DTEND:' + formattedEndTime,
        'SUMMARY:' + event.title,
        'DESCRIPTION:' + event.description,
        'LOCATION:' + event.location,
        'END:VEVENT',
        'END:VCALENDAR',
      ].join('\n');

      if (isMobileUserAgent) {
        calendarUrl = encodeURI(
          'data:text/calendar;charset=utf8,' + calendarUrl,
        );
      }
  }

  return calendarUrl;
};

export const addToCalendarHandler = (
  event: AddToCalendarEvent,
  type: CalendarType,
) => {
  const url = buildEventUrl(event, type);

  if (
    !isMobileUserAgent &&
    (url.startsWith('data') || url.startsWith('BEGIN'))
  ) {
    const filename = 'reminder.ics';
    const blob = new Blob([url], { type: 'text/calendar;charset=utf-8' });

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    window.open(url, '_blank');
  }
};
